import Header from "@/components/Header";
import { Link } from "react-router-dom";
import { ShieldCheck, ChevronLeft, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Privacy Policy — content sourced verbatim from Sathi Limited's official
 * document (NZBN 9429052213656). Do not edit copy without a legal review.
 */
export default function PrivacyPolicy() {
  const onPrint = () => window.print();
  return (
    <div className="min-h-screen bg-[#0A1A33] text-white print:bg-white print:text-black" data-testid="privacy-page">
      <div className="print:hidden">
        <Header />
      </div>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 pb-20 print:pt-8">
        <div className="flex items-center justify-between mb-4 print:hidden">
          <Link to="/" className="inline-flex items-center gap-1 text-caption text-slate-400 hover:text-red-300" data-testid="privacy-back">
            <ChevronLeft className="w-3.5 h-3.5" /> Back home
          </Link>
          <Button onClick={onPrint} size="sm" className="btn-gold rounded-full" data-testid="privacy-print">
            <Printer className="w-3.5 h-3.5 mr-1.5" /> Download PDF
          </Button>
        </div>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-red-500/15 border border-red-400/30 grid place-items-center">
            <ShieldCheck className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <div className="text-caption">Legal</div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-tight">Triptap Privacy Policy</h1>
          </div>
        </div>
        <p className="text-slate-400 mt-2 text-sm">Operated by Sathi Limited · NZBN 9429052213656 · Compliant with the Privacy Act 2020.</p>

        <article className="mt-10 glass-card gold-border rounded-2xl p-6 sm:p-10 space-y-8 fade-up">
          <Section n="1" title="Introduction">
            <p>Our privacy policy complies with Privacy Act 2020. Your privacy is important to us, and we protect the information you share with us. Personal Information includes names, addresses, email addresses, etc.</p>
          </Section>

          <Section n="2" title="Information Collection and Use">
            <Bullets items={[
              ["Data We Collect", "We collect information about you, your device, and your activities when you use our App. This includes IP addresses, device information, and personal details you provide."],
              ["Purpose", "This information helps us fulfil your requirements and deliver a personalized experience."],
            ]} />
          </Section>

          <Section n="3" title="Notice">
            <Bullets items={[
              ["When We Ask for Information", "We will ask for personal information when needed, such as during registration or use of certain features."],
            ]} />
          </Section>

          <Section n="4" title="Consent and Sharing">
            <Bullets items={[
              ["Sharing Information", "We share collected information with stakeholders like customers, third parties, employees, etc., and may share information if required by law."],
            ]} />
          </Section>

          <Section n="5" title="Access">
            <Bullets items={[
              ["Review and Update", "You can review and update your personal information at any time through the App's login section."],
            ]} />
          </Section>

          <Section n="6" title="Security">
            <Bullets items={[
              ["Protecting Your Information", "We take strong measures to protect your data from unauthorized access. However, we do not guarantee complete security for e-commerce transactions."],
              ["Payment Issues", "Payment-related complaints are the responsibility of the payment service providers (Swift) and ANZ bank, and users must agree to their terms and conditions."],
            ]} />
          </Section>

          <Section n="7" title="Notice to Parents">
            <Bullets items={[
              ["Children's Privacy", "We encourage parents to guide their children about safe use of personal information. We do not publish harmful content but will act to remove unauthorized information from children."],
            ]} />
          </Section>

          <Section n="8" title="Cookies">
            <Bullets items={[
              ["Use of Cookies", "The App may place cookies on your mobile device to store preferences, collect statistical data, and track your use of the service. Cookies can be adjusted in your browser settings, but some features may not function properly if cookies are turned off."],
            ]} />
          </Section>

          <Section n="9" title="What we do with information you share">
            <p><b>Usage of Information:</b> When you join, you provide contact information which we use to connect users and service providers.</p>
            <Bullets items={[
              ["Disclosure", "Personal information will only be disclosed if required by law or to protect the rights and safety of users."],
            ]} />
          </Section>

          <Section n="10" title="Information sharing and disclosure">
            <Bullets items={[
              ["No Selling or Renting", "Personal Information is not rented or sold to non-affiliated companies, except to provide requested services."],
              ["Trusted Partners", "Information may be shared with trusted partners to enhance service experience, and they do not have independent rights to share this information."],
              ["Legal Compliance", "Information may be shared to comply with legal processes, investigate illegal activities, or if the company is acquired."],
              ["Disclosure to SWIFT", "Personal information may be disclosed to Online Payment Gateway Service Providers for preventing fraud, resolving disputes, and processing payments."],
            ]} />
          </Section>

          <Section n="11" title="Confidentiality and Security">
            <Bullets items={[
              ["Access Limitation", "Access to Personal Information is limited to employees who need it to provide services or perform their jobs."],
              ["Safeguards", "The company uses physical, electronic, and procedural safeguards to protect personal information, although 100% security cannot be guaranteed."],
            ]} />
          </Section>

          <Section n="12" title="Your Consent">
            <Bullets items={[
              ["Consent", "By using the App, you consent to the terms of the Privacy Policy and the processing of Personal Information as described."],
            ]} />
          </Section>

          <Section n="13" title="Contact Information">
            <Bullets items={[
              ["Feedback", <>The company welcomes comments regarding the privacy statement at <a href="mailto:ttriptap@gmail.com" className="text-red-300 hover:text-red-200 underline underline-offset-2">ttriptap@gmail.com</a>.</>],
            ]} />
          </Section>

          <Section n="14" title="Permissions">
            <Bullets items={[
              ["Permissions Used", "The App uses certain permissions like camera and location solely for input purposes and does not store any personal information."],
              ["Location Permission", "Location permissions are used for accurate delivery and tracking services, and all data is secured under Google consents."],
            ]} />
          </Section>

          <div className="pt-6 border-t border-white/10 text-xs text-slate-500">
            Last updated: {new Date().toLocaleDateString("en-NZ", { year: "numeric", month: "long", day: "numeric" })}
          </div>
        </article>
      </main>
    </div>
  );
}

const Section = ({ n, title, children }) => (
  <section className="scroll-mt-24" id={`section-${n}`}>
    <div className="flex items-baseline gap-3 mb-3">
      <span className="text-red-400 font-mono text-sm">§{n}</span>
      <h2 className="font-display font-bold text-xl sm:text-2xl text-white">{title}</h2>
    </div>
    <div className="space-y-3 text-slate-300 leading-relaxed text-[15px]">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2">
    {items.map(([label, body], i) => (
      <li key={i} className="flex gap-2">
        <span className="text-red-400 mt-1.5 flex-shrink-0 w-1 h-1 rounded-full bg-red-400"></span>
        <span><b className="text-white">{label}:</b> {body}</span>
      </li>
    ))}
  </ul>
);
